import { IDepartment, ISector, ILocation, IProgramme, IObjectiveArea, ILookupAPIResp, IDistrict } from './../../../../core/_models';
import { requestLaravel } from "../../../../../../utils/api";
import { ApiResponse } from "../../../../core/_models";
import type { IStakeHolder, StakeholderApi, StakeholderDeleteResult } from "../../common/StakeholderEngagement/types";
import { getProjectFieldSection, saveProjectFieldSection, withProjectFieldValues } from "../../../core/project-fields/_request";
import { getProblemTree } from "../../common/ProblemTree/_request";
import { ProjectSectionItem, SectionItemInput, SectionItemScope, createSectionItem, deleteSectionItem, listSectionItems, sectionItemKeyText, sectionItemKeyValue, updateSectionItem } from "../../../core/section-items/_request";
import { ICoreProblemAndGapAnalysis, IEvidenceBase, IExecutiveSummary, IFiveWOneH, IStrategicSignificanceAndImpactFrameworkStatistics, IProblemIdentification, IProblemTreeAnalysis, IMeansToEndResponse, IObjectiveMean, IObjectiveEnd, IProjectOutput, IObjectiveSmartIndicator, ISGOActivitiesAndQuests, IProjectOverview, IWithProjectScenario, IKeyBusinessDrivers, IStrategicNeed, IStakeholderTable, IKeyActivitiesAndQuestsResponse, IUniqueValuePropositionCompetitiveAdvantageInvestmentOpportunity, IProcurementofPlanningResources, IConstraintsAndRisksResponse, PROJECT_OVERVIEW_SECTION_FIELDS, ProjectOverviewSection, IInstitutionalSponsor, IProjectAlternative, STAKEHOLDER_ANALYSIS_SECTION, IMajorInput, StepTab, IProjectObjectivesAndStrategicAlignment, OBJECTIVES_SECTION, IProjectResourceRequirements, RESOURCE_REQUIREMENTS_SECTION, IPreliminaryProcurementStrategy, PROCUREMENT_STRATEGY_SECTION, IProjectOutcome } from './_models';

const PROJECT_INITIATION_STAGE_ID = 0;
// Stage key used by project section items, KPI indicators and outcomes.
const STAGE_ZERO_KEY = "project_stage_0";

function readStageSection<T>(projectId: string | number, sectionKey: string, signal?: AbortSignal) {
    return getProjectFieldSection(projectId, PROJECT_INITIATION_STAGE_ID, sectionKey, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

function writeStageSection<T>(projectId: string | number, sectionKey: string, payload: object, signal?: AbortSignal) {
    return saveProjectFieldSection(projectId, PROJECT_INITIATION_STAGE_ID, sectionKey, payload, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

type IMasterData = {
    departments: IDepartment[];
    sectors: ISector[];
    districts: IDistrict[];
    priorities: ILookupAPIResp;
    project_types: ILookupAPIResp;
    action_project_types: ILookupAPIResp;
    jurisdiction: ILookupAPIResp;
    locations: ILocation[];
    programmes: IProgramme[];
    idms_project_stage: ILookupAPIResp;
};

export function getAllMasterData(signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IMasterData>>("/project-stage-zero/master-data/list", {}, { signal });
}

export function getProjectInitiation(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectOverview>>("/project-stage-zero/overview/get", { project_id }, { signal });
}

export function getExecutiveSummeryData(project_id: string, signal?: AbortSignal) {
    return readStageSection<IExecutiveSummary>(project_id, "executive_summary", signal);
}

type UpdateProjectOverviewPayload = Partial<IProjectOverview> & {
    project_id: number;
    section: ProjectOverviewSection;
};

// Saves one Project Overview tab; the response holds only that tab's fields.
export function updateProjectOverview(project_id: number, section: ProjectOverviewSection, values: IProjectOverview) {
    const payload: UpdateProjectOverviewPayload = { project_id, section };
    PROJECT_OVERVIEW_SECTION_FIELDS[section].forEach((field) => {
        (payload as Record<string, unknown>)[field] = values[field];
    });
    return requestLaravel<ApiResponse<Partial<IProjectOverview>>>("/project-stage-zero/overview/update", payload);
}

interface UpdateExecutiveSummaryPayload extends IExecutiveSummary {
    project_id: number;
}
export function updateExecutiveSummary(updateData: UpdateExecutiveSummaryPayload) {
    return writeStageSection<IExecutiveSummary>(updateData.project_id, "executive_summary", updateData);
}

export function getFiveWOneHData(project_id: string, signal?: AbortSignal) {
    return readStageSection<IFiveWOneH>(project_id, "ex_5w1h", signal);
}

interface UpdateFiveWOneHPayload extends IFiveWOneH {
    project_id: number;
}
export function updateFiveWOneH(updateData: UpdateFiveWOneHPayload) {
    return writeStageSection<IFiveWOneH>(updateData.project_id, "ex_5w1h", updateData);
}

export function getProblemIdentificationData(project_id: string, signal?: AbortSignal) {
    return getProjectFieldSection(project_id, PROJECT_INITIATION_STAGE_ID, "problem_identification", signal);
}

interface UpdateProblemIdentificationPayload extends IProblemIdentification {
    project_id: number;
}

export function updateProblemIdentification(updateData: UpdateProblemIdentificationPayload) {
    return writeStageSection<IProblemIdentification>(updateData.project_id, "problem_identification", updateData);
}

export function getProblemTreeAnalysisData(project_id: string, signal?: AbortSignal) {
    return readStageSection<Partial<IProblemTreeAnalysis>>(project_id, "problem_tree_analysis", signal);
}

// Saves one tab of a tab-wise step through its project-fields sub-section, sending only the
// tab's fields; the response holds only that tab's fields.
export function saveStepTab(project_id: number, tab: StepTab & { section: string }, values: object) {
    const payload: Record<string, unknown> = {};
    tab.fields.forEach((field) => {
        payload[field] = (values as Record<string, unknown>)[field];
    });
    return writeStageSection<Record<string, unknown>>(project_id, tab.section, payload);
}

export function getProcurementStrategyData(project_id: string, signal?: AbortSignal) {
    return readStageSection<Partial<IPreliminaryProcurementStrategy>>(project_id, PROCUREMENT_STRATEGY_SECTION, signal);
}

export function getResourceRequirementsData(project_id: string, signal?: AbortSignal) {
    return readStageSection<Partial<IProjectResourceRequirements>>(project_id, RESOURCE_REQUIREMENTS_SECTION, signal);
}

export function getObjectivesAlignmentData(project_id: string, signal?: AbortSignal) {
    return readStageSection<Partial<IProjectObjectivesAndStrategicAlignment>>(project_id, OBJECTIVES_SECTION, signal);
}

export function getProblemTreeData(project_id: string, signal?: AbortSignal) {
    return getProblemTree("project-stage-zero", project_id, signal);
}

export function getMeansToEndData(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IMeansToEndResponse>>("/project-stage-zero/means-to-end/get", { project_id }, { signal });
}

export type SaveObjectiveMeanPayload = {
    project_id: number;
    mean_id: number | null;
    title: string;
    description: string | null;
    cause_item_id: number | null;
};

export function saveObjectiveMean(payload: SaveObjectiveMeanPayload) {
    return requestLaravel<ApiResponse<IObjectiveMean>>("/project-stage-zero/means-to-end/means/save", payload);
}

export function deleteObjectiveMean(mean_id: number, project_id: number) {
    return requestLaravel<ApiResponse<{ mean_id: number }>>("/project-stage-zero/means-to-end/means/delete", { mean_id, project_id });
}

export type SaveObjectiveEndPayload = {
    project_id: number;
    end_id: number | null;
    title: string;
    description: string | null;
    effect_item_id: number | null;
    mean_ids: number[];
};

export function saveObjectiveEnd(payload: SaveObjectiveEndPayload) {
    return requestLaravel<ApiResponse<IObjectiveEnd>>("/project-stage-zero/means-to-end/ends/save", payload);
}

export function deleteObjectiveEnd(end_id: number, project_id: number) {
    return requestLaravel<ApiResponse<{ end_id: number }>>("/project-stage-zero/means-to-end/ends/delete", { end_id, project_id });
}

// A project outcome as the outcomes API returns it. A WCG outcome keeps its "outcomes" lookup
// option in outcome_key, and outcome holds that option's name.
type ProjectOutcomeRow = {
    outcome_id: number;
    outcome_type: "project_outcome" | "wcg_outcome";
    outcome_key: string | null;
    outcome: string | null;
    description: string | null;
    // Missing until the API returns the outcome's project objectives.
    objective_row_ids?: number[] | null;
};

const toProjectOutcome = (row: ProjectOutcomeRow): IProjectOutcome => ({
    id: row.outcome_id,
    outcome_type: row.outcome_type === "wcg_outcome" ? "wcg" : "project",
    wcg_outcome: row.outcome_key ?? "",
    outcome: row.outcome ?? "",
    description: row.description ?? "",
    objective_row_ids: (row.objective_row_ids ?? []).map(Number),
});

export async function getProjectOutcomesData(project_id: string, signal?: AbortSignal, stage_id: string = STAGE_ZERO_KEY): Promise<ApiResponse<IProjectOutcome[]>> {
    const response = await requestLaravel<ApiResponse<ProjectOutcomeRow[]>>("/project/outcomes/list", { project_id: Number(project_id), stage_id }, { signal });
    return { ...response, data: (response.data ?? []).map(toProjectOutcome) };
}

// Creates the outcome when id is null, otherwise updates it.
export async function saveProjectOutcome(
    project_id: number,
    outcome: Omit<IProjectOutcome, "id"> & { id: number | null },
    stage_id: string = STAGE_ZERO_KEY,
): Promise<ApiResponse<IProjectOutcome>> {
    const isWcg = outcome.outcome_type === "wcg";
    const response = await requestLaravel<ApiResponse<ProjectOutcomeRow>>("/project/outcomes/save", {
        project_id,
        stage_id,
        outcome_id: outcome.id,
        outcome_type: isWcg ? "wcg_outcome" : "project_outcome",
        outcome_key: isWcg ? outcome.wcg_outcome : null,
        outcome: isWcg ? null : outcome.outcome,
        description: outcome.description || null,
        objective_row_ids: outcome.objective_row_ids,
    });
    return { ...response, data: response.data ? toProjectOutcome(response.data) : response.data };
}

export async function deleteProjectOutcome(outcome_id: number, project_id: number, stage_id: string = STAGE_ZERO_KEY): Promise<ApiResponse<{ outcome_id: number }>> {
    const response = await requestLaravel<ApiResponse<[]>>("/project/outcomes/delete", { outcome_id, project_id, stage_id });
    return { ...response, data: { outcome_id } };
}

// Project outputs are project section items; output_id is the item id.
const projectOutputScope = (project_id: number | string): SectionItemScope => ({
    project_id: Number(project_id),
    stage_id: STAGE_ZERO_KEY,
    section: "project_output",
});

const toProjectOutput = (item: ProjectSectionItem): IProjectOutput => ({
    output_id: item.id,
    title: item.title,
    description: item.description,
});

export async function getProjectOutputsData(project_id: string, signal?: AbortSignal): Promise<ApiResponse<IProjectOutput[]>> {
    const response = await listSectionItems(projectOutputScope(project_id), signal);
    return { ...response, data: (response.data ?? []).map(toProjectOutput) };
}

export type SaveProjectOutputPayload = {
    project_id: number;
    output_id: number | null;
    title: string;
    description: string | null;
};

// Creates the output when output_id is null, otherwise updates it.
export async function saveProjectOutput(payload: SaveProjectOutputPayload): Promise<ApiResponse<IProjectOutput>> {
    const scope = projectOutputScope(payload.project_id);
    const item = { title: payload.title, description: payload.description };
    const response = payload.output_id === null
        ? await createSectionItem(scope, item)
        : await updateSectionItem(scope, payload.output_id, item);
    return { ...response, data: response.data ? toProjectOutput(response.data) : response.data };
}

export async function deleteProjectOutput(output_id: number, project_id: number): Promise<ApiResponse<{ output_id: number }>> {
    const response = await deleteSectionItem(projectOutputScope(project_id), output_id);
    return { ...response, data: { output_id } };
}

export function getMajorInputsData(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IMajorInput[]>>("/project-stage-zero/major-inputs/list", { project_id }, { signal });
}

export type SaveMajorInputPayload = Omit<IMajorInput, 'major_input_id'> & {
    project_id: number;
    major_input_id: number | null;
};

export function saveMajorInput(payload: SaveMajorInputPayload) {
    return requestLaravel<ApiResponse<IMajorInput>>("/project-stage-zero/major-inputs/save", payload);
}

export function deleteMajorInput(major_input_id: number, project_id: number) {
    return requestLaravel<ApiResponse<{ major_input_id: number }>>("/project-stage-zero/major-inputs/delete", { major_input_id, project_id });
}

// Indicators listed before the API returned outcome links have none, and those saved before
// timeframes existed have no timeframe.
const toSmartIndicator = (row: IObjectiveSmartIndicator): IObjectiveSmartIndicator => ({
    ...row,
    target_timeframe_years: row.target_timeframe_years == null ? null : Number(row.target_timeframe_years),
    outcome_ids: (row.outcome_ids ?? []).map(Number),
});

export async function getSmartIndicatorsData(project_id: string, signal?: AbortSignal, stage_id: string = STAGE_ZERO_KEY): Promise<ApiResponse<IObjectiveSmartIndicator[]>> {
    const response = await requestLaravel<ApiResponse<IObjectiveSmartIndicator[]>>("/project/kpi-indicators/list", { project_id, stage_id }, { signal });
    return { ...response, data: (response.data ?? []).map(toSmartIndicator) };
}

export type SaveSmartIndicatorPayload = {
    project_id: number;
    kpis_indicator_id: number | null;
    kpi: string;
    measure: string;
    baseline: string;
    target: string;
    target_timeframe_years: number;
    target_date: string | null;
};

export async function saveSmartIndicator(payload: SaveSmartIndicatorPayload, stage_id: string = STAGE_ZERO_KEY): Promise<ApiResponse<IObjectiveSmartIndicator>> {
    const response = await requestLaravel<ApiResponse<IObjectiveSmartIndicator>>("/project/kpi-indicators/save", { ...payload, stage_id });
    return { ...response, data: response.data ? toSmartIndicator(response.data) : response.data };
}

export function deleteSmartIndicator(kpis_indicator_id: number, project_id: number, stage_id: string = STAGE_ZERO_KEY) {
    return requestLaravel<ApiResponse<{ kpis_indicator_id: number }>>("/project/kpi-indicators/delete", { kpis_indicator_id, project_id, stage_id });
}

// Sets the one indicator that measures a project outcome; null clears it.
export function assignSmartIndicator(project_id: number, outcome_id: number, kpis_indicator_id: number | null, stage_id: string = STAGE_ZERO_KEY) {
    return requestLaravel<ApiResponse<{ outcome_id: number; kpis_indicator_id: number | null }>>("/project/kpi-indicators/assign-outcome", { project_id, stage_id, outcome_id, kpis_indicator_id });
}

export function getEvidenceBaseData(project_id: string, signal?: AbortSignal) {
    return readStageSection<IEvidenceBase>(project_id, "evidence_base", signal);
}

interface UpdateEvidenceBasePayload extends IEvidenceBase {
    project_id: number;
}
export function updateEvidenceBase(updateData: UpdateEvidenceBasePayload) {
    return writeStageSection<IEvidenceBase>(updateData.project_id, "evidence_base", updateData);
}

function updateprojectstage0minieditor<
    TResponse,
    TPayload extends { project_id: number }
>(
    sectionKey: string,
    updateData: TPayload,
    signal?: AbortSignal,
) {
    return writeStageSection<TResponse>(updateData.project_id, sectionKey, updateData, signal);
}

function getprojectstage0minieditor<TResponse>(
    sectionKey: string,
    projectId: number,
    signal?: AbortSignal,
) {
    return readStageSection<TResponse>(projectId, sectionKey, signal);
}

interface UpdateWithProjectScenarioPayload {
    project_id: number;
    desired_state_description: string;
    desired_state_quantified: string;
}

export function updateWithProjectScenario(
    updateData: UpdateWithProjectScenarioPayload
) {
    return updateprojectstage0minieditor<IWithProjectScenario, UpdateWithProjectScenarioPayload>("with_project_scenario", updateData)
}

export function getWithProjectScenarioData(payload: UpdateWithProjectScenarioPayload, signal?: AbortSignal) {
    return getprojectstage0minieditor<IWithProjectScenario>("with_project_scenario", payload.project_id, signal)
}

interface UpdatekeyBusinessDriverPayload {
    project_id: number
    kbd_drivers_of_intervention_need: string | number;
}

export function updatekeyBusinessDriverPayload(
    updateData: UpdatekeyBusinessDriverPayload
) {
    return updateprojectstage0minieditor<IKeyBusinessDrivers, UpdatekeyBusinessDriverPayload>("key_business_drivers", updateData)
}

export function getkeyBusinessDriverPayload(payload: UpdatekeyBusinessDriverPayload, signal?: AbortSignal) {
    return getprojectstage0minieditor<IKeyBusinessDrivers>("key_business_drivers", payload.project_id, signal)
}

interface UpdateStrategicNeedPayload extends IStrategicNeed {
    project_id: number;
}

export function updateStrategicNeed(updateData: UpdateStrategicNeedPayload) {
    return updateprojectstage0minieditor<IStrategicNeed, UpdateStrategicNeedPayload>("strategic_need", updateData)
}

export function getStrategicNeedData(project_id: number, signal?: AbortSignal) {
    return getprojectstage0minieditor<IStrategicNeed>("strategic_need", project_id, signal)
}

interface GetCoreProblemAndGapAnalysisPayload {
    project_id: number;
    core_problem_statement: string;
    cause_effect_logic: string;
    consequences: string;
}
export function getCoreProblemAndGapAnalysisData(payload: GetCoreProblemAndGapAnalysisPayload, signal?: AbortSignal) {
    return getprojectstage0minieditor<ICoreProblemAndGapAnalysis>("core_problem_and_gap_analysis", payload.project_id, signal)
}

interface UpdateCoreProblemAndGapAnalysisPayload extends ICoreProblemAndGapAnalysis {
    project_id: number;
}
export function updateCoreProblemAndGapAnalysis(updateData: UpdateCoreProblemAndGapAnalysisPayload) {
    return updateprojectstage0minieditor<ICoreProblemAndGapAnalysis, UpdateCoreProblemAndGapAnalysisPayload>("core_problem_and_gap_analysis", updateData)
}

type UpdateProjectObjectivesPayload = Omit<

    IObjectiveArea,
    "keyFocus_name"
>
export function storeOrUpdateProjectObjectives(payload: UpdateProjectObjectivesPayload, stage_id: string = STAGE_ZERO_KEY) {
    return requestLaravel<ApiResponse<IObjectiveArea>>("/project/objectives/save", { ...payload, stage_id });
}

export function getProjectObjectivesData(project_id: number, signal?: AbortSignal, stage_id: string = STAGE_ZERO_KEY) {
    return requestLaravel<ApiResponse<IObjectiveArea[]>>("/project/objectives/list", { project_id, stage_id }, { signal });
}

export function deleteProjectObjectivesData(row_id: number, project_id: number, stage_id: string = STAGE_ZERO_KEY) {
    return requestLaravel<ApiResponse<any>>("/project/objectives/delete", { row_id, project_id, stage_id });
}

export function getProjectStakeholdersData(project_id: number, signal?: AbortSignal, stage_id: string = STAGE_ZERO_KEY) {
    return requestLaravel<ApiResponse<IStakeholderTable[]>>("/project/stakeholders/list", { project_id, stage_id }, { signal });
}

// Stakeholders of a stage (each stage keeps its own and checks its own rights).
export const stakeholderApi = (stage_id: string): StakeholderApi => ({
    list: (project_id, signal) =>
        requestLaravel<ApiResponse<IStakeHolder[]>>("/project/stakeholders/list", { project_id, stage_id }, { signal }),
    save: (payload, signal) =>
        requestLaravel<ApiResponse<IStakeHolder>>("/project/stakeholders/save", { ...payload, stage_id }, { signal }),
    remove: (stakeholder_id, project_id, signal) =>
        requestLaravel<ApiResponse<StakeholderDeleteResult>>("/project/stakeholders/delete", { stakeholder_id, project_id, stage_id }, { signal }),
});

export const stageZeroStakeholderApi = stakeholderApi(STAGE_ZERO_KEY);


export function storeOrUpdateKeyActivitiesAndQuestsData(payload: ISGOActivitiesAndQuests & { project_id: number }) {
    return requestLaravel<ApiResponse<ISGOActivitiesAndQuests>>("/project-stage-zero/key-activities-and-quests/save", payload);
}

export function getKeyActivitiesAndQuestsData(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IKeyActivitiesAndQuestsResponse>>("/project-stage-zero/key-activities-and-quests/get", { project_id }, { signal });
}

export function getStrategicFitSummaryDashboardData(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IStrategicSignificanceAndImpactFrameworkStatistics>>("/project-stage-zero/strategic-fit-summary/get", { project_id }, { signal });
}

export function getSystemIntelligenceLayerDataData(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<any>>("/project-stage-zero/system-intelligence-layer/get", { project_id }, { signal });
}


export function saveProjectStageZero(payload: { project_id: string; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<[]>>("/project-stage-zero/stage/submit", { project_id: payload.project_id }, { signal });
}

interface UpdateUVPPayload extends IUniqueValuePropositionCompetitiveAdvantageInvestmentOpportunity {
    project_id: number;
}

// Sends only the UVP step's own fields; the beneficiary groups shown in the section are read-only there.
export function updateUniqueValueProposition(updateData: UpdateUVPPayload) {
    const { customer_segment, customer_need, unique_value_proposition, evidence_of_value, strategic_positioning, competitive_advantage } = updateData;
    return writeStageSection<IUniqueValuePropositionCompetitiveAdvantageInvestmentOpportunity>(
        updateData.project_id,
        "unique_value_proposition_assessment",
        { customer_segment, customer_need, unique_value_proposition, evidence_of_value, strategic_positioning, competitive_advantage },
    );
}

// The whole Stakeholder Analysis section with its sub-sections; raw, so its options can be shown.
export function getStakeholderAnalysisData(project_id: string, signal?: AbortSignal) {
    return getProjectFieldSection(project_id, PROJECT_INITIATION_STAGE_ID, STAKEHOLDER_ANALYSIS_SECTION, signal);
}

// Project alternatives are project section items, listed in the order they were added. An option's
// number is its position, so deleting one moves the later options up; the title ("Option 3") mirrors it.
const projectAlternativeScope = (project_id: number | string): SectionItemScope => ({
    project_id: Number(project_id),
    stage_id: STAGE_ZERO_KEY,
    section: "project_alternative",
});

// Each preliminary cost estimate is a key named after its preliminary_cost_estimation_option
// data_key, holding that option with the editor text as free_text.
const COST_ESTIMATE_KEY_PREFIX = "preliminary_cost_estimation_option_";

const projectAlternativeTitle = (optionNo: number): string => `Option ${optionNo}`;

const toProjectAlternative = (item: ProjectSectionItem, optionNo: number): IProjectAlternative => ({
    id: item.id,
    option_no: optionNo,
    description: item.description ?? "",
    cost_estimates: Object.fromEntries(
        Object.keys(item.keys ?? {})
            .filter((keyName) => keyName.startsWith(COST_ESTIMATE_KEY_PREFIX))
            .map((keyName) => [keyName, sectionItemKeyText(item, keyName)])
    ),
});

export async function getProjectAlternativesData(project_id: string, signal?: AbortSignal): Promise<ApiResponse<IProjectAlternative[]>> {
    const response = await listSectionItems(projectAlternativeScope(project_id), signal);
    return { ...response, data: (response.data ?? []).map((item, index) => toProjectAlternative(item, index + 1)) };
}

// Creates the option when id is null, otherwise updates it; its cost estimates are left as they are.
export async function saveProjectAlternative(
    project_id: string,
    option: Omit<IProjectAlternative, "id" | "cost_estimates"> & { id: number | null },
): Promise<ApiResponse<IProjectAlternative>> {
    const scope = projectAlternativeScope(project_id);
    const item = { title: projectAlternativeTitle(option.option_no), description: option.description };
    const response = option.id === null
        ? await createSectionItem(scope, item)
        : await updateSectionItem(scope, option.id, item);
    return { ...response, data: response.data ? toProjectAlternative(response.data, option.option_no) : response.data };
}

// Saves one preliminary cost estimate of an option; empty text clears it. The option's other estimates are left as they are.
export async function saveProjectAlternativeEstimate(
    project_id: string,
    option: IProjectAlternative,
    estimateKey: string,
    text: string,
): Promise<ApiResponse<IProjectAlternative>> {
    const response = await updateSectionItem(projectAlternativeScope(project_id), option.id, {
        keys: { [estimateKey]: text ? { key_value: estimateKey, free_text: text } : null },
    });
    return { ...response, data: response.data ? toProjectAlternative(response.data, option.option_no) : response.data };
}

export function deleteProjectAlternative(project_id: string, id: number) {
    return deleteSectionItem(projectAlternativeScope(project_id), id);
}

// Renumbers the options left after a delete and saves the title of each one that moved up.
export async function renumberProjectAlternatives(project_id: string, options: IProjectAlternative[]): Promise<IProjectAlternative[]> {
    const scope = projectAlternativeScope(project_id);
    const renumbered = options.map((option, index) => ({ ...option, option_no: index + 1 }));
    await Promise.all(renumbered
        .filter((option, index) => option.option_no !== options[index].option_no)
        .map((option) => updateSectionItem(scope, option.id, { title: projectAlternativeTitle(option.option_no) })));
    return renumbered;
}

// Institutional sponsors are project section items; their type is the item's "type" key.
export const institutionalSponsorScope = (project_id: number | string, stage_id: string = STAGE_ZERO_KEY): SectionItemScope => ({
    project_id: Number(project_id),
    stage_id,
    section: "institutional_sponsor",
});

export const toInstitutionalSponsor = (item: ProjectSectionItem): IInstitutionalSponsor => ({
    id: item.id,
    name: item.title,
    type: sectionItemKeyValue(item, "type"),
    description: item.description ?? "",
});

export const toInstitutionalSponsorInput = (sponsor: Omit<IInstitutionalSponsor, "id">): SectionItemInput => ({
    title: sponsor.name,
    description: sponsor.description || null,
    keys: { type: sponsor.type },
});

// A beneficiary group selected under Stakeholder Analysis, as the beneficiary-groups API returns it.
type BeneficiaryGroupRow = { key_value: string; display_name: string | null; free_text: string };

// The selected beneficiary groups (lookup order) and their remarks, keyed by group.
export async function getBeneficiaryGroupsData(
    project_id: number,
    signal?: AbortSignal,
): Promise<ApiResponse<{ groups: string[]; remarks: Record<string, string> }>> {
    const response = await requestLaravel<ApiResponse<BeneficiaryGroupRow[]>>("/project-stage-zero/beneficiary-groups/get", { project_id }, { signal });
    const rows = response.data ?? [];
    return {
        ...response,
        data: {
            groups: rows.map((row) => row.key_value),
            remarks: Object.fromEntries(rows.filter((row) => row.free_text).map((row) => [row.key_value, row.free_text])),
        },
    };
}

export function getUniqueValueProposition(payload: { project_id: number }, signal?: AbortSignal) {
    return readStageSection<IUniqueValuePropositionCompetitiveAdvantageInvestmentOpportunity>(
        payload.project_id,
        "unique_value_proposition_assessment",
        signal,
    );
}


interface GetProcurementofPlanningResourcesPayload {
    project_id: number;
    request_for_additional_funding: string
}
export function getProcurementofPlanningResourcesData(payload: GetProcurementofPlanningResourcesPayload, signal?: AbortSignal) {
    return getprojectstage0minieditor<IProcurementofPlanningResources>("procurement_of_planning_resources", payload.project_id, signal)
}

interface UpdateProcurementofPlanningResourcesPayload extends IProcurementofPlanningResources {
    project_id: number;
}
export function updateProcurementofPlanningResources(updateData: UpdateProcurementofPlanningResourcesPayload) {
    return updateprojectstage0minieditor<IProcurementofPlanningResources, UpdateProcurementofPlanningResourcesPayload>("procurement_of_planning_resources", updateData)
}

export function getConstrainAndRisksData(project_id: number, signal?: AbortSignal) {
    return readStageSection<IConstraintsAndRisksResponse>(project_id, "constraints_and_risks", signal);
}

export function updateConstrainAndRisksData(
    updateData: IConstraintsAndRisksResponse & { project_id: number },
    signal?: AbortSignal
) {
    return writeStageSection<IConstraintsAndRisksResponse>(
        updateData.project_id,
        "constraints_and_risks",
        updateData,
        signal,
    );
}
